import { Button, TextInput, Loading } from '@carbon/react';
import { useState } from 'react';

export default function TestFacebookSearch() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const params = new URLSearchParams({
        q: query,
        limit: '50', // Start with smaller number for testing
      });

      const response = await fetch(`/api/facebook-search?${params.toString()}`);
      const data = await response.json() as { details?: string; error?: string };

      if (!response.ok) {
        throw new Error(data.details || data.error || 'Search failed');
      }

      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Test Facebook Search</h1>
      
      <div className="flex gap-4 mb-6">
        <TextInput
          id="search-query"
          labelText="Search Query"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter search term..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />
        <Button onClick={handleSearch} disabled={loading || !query.trim()}>
          {loading ? 'Searching...' : 'Search'}
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <Loading description="Searching Facebook posts..." />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-6">
          <h3 className="font-medium text-red-800">Error</h3>
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {results && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <h3 className="font-medium text-blue-800">Search Results</h3>
            <p className="text-blue-600">
              Found {results.data?.totalPosts || 0} posts for "{query}"
            </p>
            <p className="text-sm text-blue-500">
              Metadata: {JSON.stringify(results.metadata, null, 2)}
            </p>
          </div>

          {results.data?.posts && results.data.posts.length > 0 && (
            <div>
              <h3 className="text-lg font-medium mb-4">Posts ({results.data.posts.length})</h3>
              <div className="space-y-4">
                {results.data.posts.slice(0, 10).map((post: any, index: number) => (
                  <div key={post.id} className="border rounded p-4 bg-gray-50">
                    <div className="font-medium text-sm text-gray-600 mb-2">
                      Post {index + 1}: {post.id}
                    </div>
                    <div className="text-sm mb-2">
                      <strong>Message:</strong> {post.message?.substring(0, 200) || 'No message'}
                      {post.message && post.message.length > 200 ? '...' : ''}
                    </div>
                    <div className="text-xs text-gray-500">
                      <div>Created: {new Date(post.created_time).toLocaleString()}</div>
                      {post.permalink_url && (
                        <div>
                          <a href={post.permalink_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            View on Facebook
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <details className="border rounded p-4">
            <summary className="cursor-pointer font-medium">Full Response Data</summary>
            <pre className="mt-4 text-xs bg-gray-100 p-4 rounded overflow-auto">
              {JSON.stringify(results, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}